import React from 'react';
import { injectIntl } from 'react-intl';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
} from '@material-ui/core';
import { flowRight as compose, property } from 'lodash-es';
import {
  formatutcPrintMMDDYY,
  nameWithCredentials,
  nameWithParenthetical,
} from '@mummssoftware/utils/formatters';
import { mapOrderName, orderTypeMapping } from '@mummssoftware/utils/orders';
import { messages } from './Messages';

/**
 * `row.patientInfo` is guaranteed by `loadPatientVerbalApprovalsForPrint` /
 * `loadBulkUnsignedVerbalsForPrint` to be a `mumms.patient`-shaped object.
 * Consumers read only the canonical camelCase fields — no casing fallbacks.
 *
 * @typedef {import('../Print/loadPatientVerbalApprovalsForPrint').UnsignedVerbalRow} UnsignedVerbalRow
 * @typedef {mumms.patient} PatientHeader
 */

const EM_DASH = '\u2014';

/** @param {unknown} s @returns {string} */
const orDash = (s) => {
  const trimmed = s == null ? '' : String(s).trim();
  return trimmed || EM_DASH;
};

/** @type {(row: UnsignedVerbalRow) => PatientHeader} */
const patientInfoOf = property('patientInfo');
/** @type {(patient: PatientHeader) => string} */
const patientNumberOf = property('patientNumber');

/**
 * @param {PatientHeader | null | undefined} patient
 * @returns {string}
 */
const formatPatientHeader = (patient) => {
  if (!patient) return '';
  const num = (patient.patientNumber ?? '').trim();
  return nameWithParenthetical(
    patient.firstName,
    patient.lastName,
    num || undefined,
  );
};

/**
 * @param {UnsignedVerbalRow} row
 * @returns {string}
 */
const formatRequestor = (row) =>
  nameWithCredentials(
    row.requestorpersonfirstname,
    row.requestorpersonlastname,
    row.requestorpersoncredentials,
  );

/** @param {string | undefined} shortName @returns {string} */
const orderTypeLabel = (shortName) => {
  if (!shortName) return EM_DASH;
  const canonical = orderTypeMapping[shortName] ?? shortName;
  return mapOrderName[canonical] || shortName;
};

/** @type {(row: UnsignedVerbalRow) => string} */
const patientCellText = compose(orDash, formatPatientHeader, patientInfoOf);
/** @type {(row: UnsignedVerbalRow) => string} */
const requestedByText = compose(orDash, formatRequestor);
/**
 * @param {string | undefined} dt
 * @param {string} tz
 * @returns {string}
 */
const verbalDateText = (dt, tz) =>
  dt ? orDash(formatutcPrintMMDDYY(dt, tz)) : EM_DASH;

/** @param {UnsignedVerbalRow} row @returns {string} */
const rowKey = (row) =>
  `${row.entityid ?? 'row'}-${patientNumberOf(patientInfoOf(row)) ?? ''}`;

const useStyles = makeStyles({
  tableContainer: {
    width: '100%',
    maxHeight: 280,
    overflow: 'auto',
  },
  table: {
    minWidth: 600,
    width: '100%',
    tableLayout: 'fixed',
  },
  cell: {
    padding: '2px 8px',
    fontSize: '0.875rem',
  },
  patientCell: {
    minWidth: 220,
    width: '30%',
    wordBreak: 'break-word',
  },
  orderTypeCell: {
    minWidth: 160,
    width: '22%',
  },
  dateCell: {
    minWidth: 108,
    width: '14%',
    whiteSpace: 'nowrap',
  },
  requestedByCell: {
    minWidth: 280,
    width: '34%',
    whiteSpace: 'nowrap',
  },
  headerCell: {
    padding: '2px 4px',
    fontSize: '0.875rem',
    fontWeight: 600,
    backgroundColor: '#f5f5f5',
  },
});

/**
 * @typedef BulkUnsignedVerbalsSummaryTableProps
 * @prop {UnsignedVerbalRow[]} [verbals]
 * @prop {string} siteTimezone IANA timezone (e.g. `America/New_York`)
 * @prop {import('react-intl').InjectedIntl} intl
 */

/** @param {BulkUnsignedVerbalsSummaryTableProps} props */
function BulkUnsignedVerbalsSummaryTableComponent({
  verbals,
  siteTimezone,
  intl,
}) {
  const classes = useStyles();

  if (!verbals || !verbals.length) {
    return (
      <TableContainer className={classes.tableContainer} component={Paper}>
        <Table className={classes.table} size="small">
          <TableBody>
            <TableRow>
              <TableCell className={classes.cell} colSpan={4}>
                {intl.formatMessage(messages.bulkPrintSummaryEmpty)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer className={classes.tableContainer} component={Paper}>
      <Table
        className={classes.table}
        size="small"
        aria-label="Bulk unsigned verbals"
      >
        <TableHead>
          <TableRow>
            <TableCell
              className={`${classes.headerCell} ${classes.patientCell}`}
            >
              {intl.formatMessage(messages.bulkPrintSummaryPatient)}
            </TableCell>
            <TableCell
              className={`${classes.headerCell} ${classes.orderTypeCell}`}
            >
              {intl.formatMessage(messages.bulkPrintSummaryOrderType)}
            </TableCell>
            <TableCell className={`${classes.headerCell} ${classes.dateCell}`}>
              {intl.formatMessage(messages.bulkPrintSummaryDate)}
            </TableCell>
            <TableCell
              className={`${classes.headerCell} ${classes.requestedByCell}`}
            >
              {intl.formatMessage(messages.bulkPrintSummaryRequestedBy)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {verbals.map((row) => (
            <TableRow key={rowKey(row)} hover>
              <TableCell className={`${classes.cell} ${classes.patientCell}`}>
                {patientCellText(row)}
              </TableCell>
              <TableCell className={`${classes.cell} ${classes.orderTypeCell}`}>
                {orderTypeLabel(row.entityordertypeshortname)}
              </TableCell>
              <TableCell className={`${classes.cell} ${classes.dateCell}`}>
                {verbalDateText(row.verbaldatetime, siteTimezone)}
              </TableCell>
              <TableCell
                className={`${classes.cell} ${classes.requestedByCell}`}
              >
                {requestedByText(row)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export const BulkUnsignedVerbalsSummaryTable = injectIntl(
  BulkUnsignedVerbalsSummaryTableComponent,
);
